import { useEffect, useLayoutEffect } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, findIndex, get, toLower, isEmpty } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { PERIOD, DATE, RESOURCE_STATUS } from "helpers/shapes"
import { numberFormatter, smallNumberFormatter } from "helpers/formatters/numbers"
import { computeCSVData } from "helpers/csv"
import { AGGREGATION_SHAPE } from "helpers/factorClassification"
import AnalyzeHeader from "modules/analyze-header"
import AnalyzeFactorSensitivityDetails from "modules/analyze-factor-sensitivity-details"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import LoadingWrapper from "components/loading-wrapper"
import { THRESHOLD_PARAMS } from "helpers/threshold"
import NotEnoughHistoricalFactorSensitivityData from "components/not-enough-historical-factor-sensitivity-data"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import AggregationDropdown, { OPTION_SHAPE } from "components/list-item-dropdown"
import withNavigate from "hocs/withNavigate"
import { useCheckLastPreferenceRedirect } from "hooks/preferences"

import "./style.scss"

function AnalyzeFactorSensitivity({
  earliestDate,
  latestDate,
  setDate,
  selectedDate,
  startDate,
  aiSummaryStartDate,
  endDate,
  period,
  datesAdjusted,
  data,
  items,
  options,
  toggle,
  toggleOnly,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  isActive,
  fileName,
  resetDate,
  firstDate,
  selectedOption,
  trackCSVDownload,
  only,
  account,
  portfolioId,
  base,
  setPeriod,
  addNotification,
  displayTypeName,
  displayAggregationToggle,
  aggregation,
  aggregationOptions,
  setAggregationFn,
  selectedViews,
  openDialog,
  displayAddView,
  availableSelectViewFactors,
  currency,
  factorSensitivityType,
  thresholdParams,
  isOpPro,
  displayType,
  isStyleFactorsAdditive,
  passthroughResourcesStatus,
  setDisplayType,
  addSelectedView,
  removeSelectedView,
}) {
  const { wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault } = useCheckLastPreferenceRedirect()
  const navigate = useNavigate()

  useLayoutEffect(() => {
    if (isOpPro && factorSensitivityType === FACTOR_SENSITIVITY_TYPES.PREDICTED && wasRedirectedFromPreferences) {
      deletePreferenceAndRedirectToDefault()
    }
  }, [isOpPro, factorSensitivityType, wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault])

  useEffect(() => {
    const firstOrStartDate = firstDate || startDate

    if (selectedDate && (selectedDate < firstOrStartDate)) {
      resetDate()
    }
  }, [firstDate, startDate, selectedDate, resetDate])

  function makeTrackedAction(fn) {
    return (...args) => {
      fn(...args, factorSensitivityType)
    }
  }

  function handleSetDisplayType(id) {
    setDisplayType(id, factorSensitivityType)
  }

  function onTabChanged(_, id) {
    handleSetDisplayType(id)
  }

  function handleAddSelectedView(factor) {
    addSelectedView(factor.id)
    handleSetDisplayType(factor.id)
  }

  function handleRemoveSelectedView(factorId) {
    if (displayType === factorId) {
      const optionIndex = findIndex(options, option => option.id === factorId)
      handleSetDisplayType(get(options, `${optionIndex - 1}.id`))
    }
    removeSelectedView(factorId)
  }

  function redirectToPredictedFS() {
    navigate(`/${account}/p/${portfolioId}/analyze/trends/factor-sensitivity/predicted`)
  }

  let content

  if (!earliestDate || !latestDate) {
    content = <NoData title="Factor Sensitivity" className="graph" />
  } else if (isEmpty(data) && factorSensitivityType === FACTOR_SENSITIVITY_TYPES.HISTORICAL) {
    content = <NotEnoughHistoricalFactorSensitivityData {...{ assetTypeName: "portfolio", isOpPro, startDate, handleSelectPredicted: redirectToPredictedFS }} />
  } else {
    let rightLegendItems
    if (displayAggregationToggle) {
      rightLegendItems = [<AggregationDropdown key="aggregation-dropdown" setValue={setAggregationFn} value={aggregation} listSubheader="Choose display type for style factors" label="Display Type" options={aggregationOptions} classes={{ toggle: "aggregation-toggle" }} />]
    }
    content = <CardContent className={classnames("card-content")}>
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
              openDialog,
              displayAddView,
              addView: handleAddSelectedView,
              factors: availableSelectViewFactors,
              value: displayType,
              onTabChanged: onTabChanged,
              tabs: map(options, ({ id, label, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: () => handleRemoveSelectedView(id),
                label,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackCSVDownload} />],
            }}
          />
          <MultiLine
            tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} />}
            {...{
                // Number is already multiplied by 100 so can't use the smallPercentageFormatter
                tickFormat: (number) => `${smallNumberFormatter(number)}%`,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle: makeTrackedAction(toggle),
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: makeTrackedAction(toggleOnly),
                thresholdParams,
                rightLegendItems,
              }}
          />
        </div>
        <AnalyzeFactorSensitivityDetails {...{
            selectedDate,
            viewType,
            displayTypeName,
            startDate: firstDate,
            endDate,
            selectedOption,
            selectedViews,
            factorSensitivityType,
            thresholdParams,
            isStyleFactorsAdditive,
          }}
        />
      </Paper>
    </CardContent>
  }

  return <div className="modules_analyze-factor-sensitivity">
    <div className="factor-sensitivity-wrapper">
      <AnalyzeHeader
        selectedId={`${toLower(factorSensitivityType)}FactorSensitivity`}
        {...{
            adjusted: datesAdjusted,
            account,
            portfolioId,
            base,
            earliestDate,
            latestDate,
            startDate,
            aiSummaryStartDate,
            endDate,
            selectedDate,
            period,
            setPeriod,
            addNotification,
            resetDate,
            firstDate,
            isActive,
            currency,
            changeDisplayPreferenceType: "navigation.analyze.trends",
            showFilterScope: true,
          }}
      />
      <LoadingWrapper status={[passthroughResourcesStatus]}>{content}</LoadingWrapper>
    </div>
  </div>
}

AnalyzeFactorSensitivity.propTypes = {
  account: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  base: PropTypes.object,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  aiSummaryStartDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  period: PERIOD.isRequired,
  data: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  displayType: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  setPeriod: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
  setDisplayType: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  trackCSVDownload: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  firstDate: PropTypes.string,
  resetDate: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  selectedOption: PropTypes.object,
  toggleOnly: PropTypes.func.isRequired,
  only: PropTypes.string,
  displayTypeName: PropTypes.string.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  datesAdjusted: PropTypes.bool.isRequired,
  displayAggregationToggle: PropTypes.bool.isRequired,
  aggregation: AGGREGATION_SHAPE,
  aggregationOptions: PropTypes.arrayOf(OPTION_SHAPE),
  setAggregationFn: PropTypes.func.isRequired,
  selectedViews: PropTypes.object,
  addSelectedView: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  displayAddView: PropTypes.bool.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  currency: PropTypes.string,
  factorSensitivityType: PropTypes.string.isRequired,
  thresholdParams: THRESHOLD_PARAMS,
  portfolio: PropTypes.string.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  isStyleFactorsAdditive: PropTypes.bool.isRequired,
  navigate: PropTypes.func.isRequired,
  passthroughResourcesStatus: RESOURCE_STATUS.isRequired,
}

export default withNavigate(AnalyzeFactorSensitivity)
